export interface SetScore {
  team1: number;
  team2: number;
}

export const EMPTY_SCORE_SETS: SetScore[] = [
  { team1: 0, team2: 0 },
  { team1: 0, team2: 0 },
];

export function isSetPlayed(set: SetScore): boolean {
  return set.team1 > 0 || set.team2 > 0;
}

export function isSetComplete(set: SetScore): boolean {
  return set.team1 > 0 && set.team2 > 0 && set.team1 !== set.team2;
}

export function getSetWinner(set: SetScore): 1 | 2 | null {
  if (!isSetComplete(set)) return null;
  return set.team1 > set.team2 ? 1 : 2;
}

/** Third set is only needed when the first two sets are split 1-1. */
export function shouldShowThirdSet(sets: SetScore[]): boolean {
  if (sets.length < 2) return false;
  const w1 = getSetWinner(sets[0]);
  const w2 = getSetWinner(sets[1]);
  return w1 !== null && w2 !== null && w1 !== w2;
}

/** Keep two sets by default; add a third row only when sets are tied 1-1. */
export function syncSetRows(sets: SetScore[]): SetScore[] {
  const first = sets[0] ?? { team1: 0, team2: 0 };
  const second = sets[1] ?? { team1: 0, team2: 0 };
  const base = [first, second];
  if (shouldShowThirdSet(base)) {
    return [...base, sets[2] ?? { team1: 0, team2: 0 }];
  }
  return base;
}

export function countSetsWon(sets: SetScore[]): { team1: number; team2: number } {
  let team1 = 0;
  let team2 = 0;
  for (const set of sets.filter(isSetPlayed)) {
    if (set.team1 > set.team2) team1 += 1;
    else if (set.team2 > set.team1) team2 += 1;
  }
  return { team1, team2 };
}

export function sumGameScores(sets: SetScore[]): { team1: number; team2: number } {
  const played = sets.filter(isSetPlayed);
  return {
    team1: played.reduce((sum, set) => sum + (set.team1 || 0), 0),
    team2: played.reduce((sum, set) => sum + (set.team2 || 0), 0),
  };
}

export function validateScoreSets(sets: SetScore[]): string | null {
  const played = sets.filter(isSetPlayed);
  if (played.length < 2) {
    return 'Enter scores for at least the first two sets';
  }
  for (let i = 0; i < played.length; i += 1) {
    const set = played[i];
    if (set.team1 === 0 && set.team2 === 0) {
      return `Set ${i + 1} cannot be 0-0`;
    }
    if (set.team1 === set.team2) {
      return `Set ${i + 1} must have a winner`;
    }
  }
  const setsWon = countSetsWon(sets);
  if (setsWon.team1 < 2 && setsWon.team2 < 2) {
    return 'A team must win at least 2 sets';
  }
  return null;
}

export function formatSetsWonScore(
  sets?: Array<{ team1: number; team2: number }> | null
): string | null {
  if (!sets?.length) return null;
  const { team1, team2 } = countSetsWon(
    sets.map((set) => ({ team1: set.team1 ?? 0, team2: set.team2 ?? 0 }))
  );
  if (team1 === 0 && team2 === 0) return null;
  return `${team1}-${team2}`;
}

/** True when aggregate scores already look like sets won (e.g. 2-0), not game totals (12-4). */
export function looksLikeSetsWonScore(team1: number, team2: number): boolean {
  return (
    Number.isFinite(team1) &&
    Number.isFinite(team2) &&
    team1 >= 0 &&
    team2 >= 0 &&
    team1 <= 2 &&
    team2 <= 2 &&
    team1 !== team2
  );
}

/** Compact match result: sets won (2-0), never combined game points (12-4). */
export function formatMatchScoreDisplay(match: {
  sets?: Array<{ team1: number; team2: number }> | null;
  team1_score?: number | null;
  team2_score?: number | null;
}): string | null {
  const fromSets = formatSetsWonScore(match.sets);
  if (fromSets) return fromSets;
  const t1 = match.team1_score;
  const t2 = match.team2_score;
  if (t1 != null && t2 != null && looksLikeSetsWonScore(t1, t2)) {
    return `${t1}-${t2}`;
  }
  return null;
}

export function buildScoreSubmitPayload(sets: SetScore[]): {
  sets: SetScore[];
  team1_score: number;
  team2_score: number;
  winner_team: 1 | 2;
} {
  const validationError = validateScoreSets(sets);
  if (validationError) {
    throw new Error(validationError);
  }
  const playedSets = sets.filter(isSetPlayed);
  const gameTotals = sumGameScores(sets);
  const setsWon = countSetsWon(sets);
  return {
    sets: playedSets,
    team1_score: gameTotals.team1,
    team2_score: gameTotals.team2,
    winner_team: setsWon.team1 >= 2 ? 1 : 2,
  };
}

export function applySetChange(
  sets: SetScore[],
  setIndex: number,
  field: 'team1' | 'team2',
  value: number
): SetScore[] {
  const padded = [...sets];
  while (padded.length < 2) {
    padded.push({ team1: 0, team2: 0 });
  }
  if (padded.length > 3) {
    padded.length = 3;
  }
  padded[setIndex] = { ...padded[setIndex], [field]: value };
  return syncSetRows(padded);
}

export function setsFromStored(stored?: SetScore[] | null): SetScore[] {
  if (!stored?.length) {
    return [...EMPTY_SCORE_SETS];
  }
  return syncSetRows(stored);
}
